import { useId, useState } from "react";
import type { RefObject } from "react";
import type { EmployeeStatus } from "@/lib/api";
import type { QueueEmployee, TransitionBody } from "@/lib/staff-api";
import { formatCalendarDate, STATUS_LABELS } from "@/lib/roster";
import { useRefreshQueues, useStaffTransition } from "@/lib/staff-hooks";
import {
  latestClearanceDate,
  setStageTargets,
  stageClearsClearance,
  stageNeedsClearanceDate,
  stageNeedsPhoto,
} from "@/lib/staff-stages";
import { ActionError } from "./ActionError";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "./buttons";
import { StaffDialog } from "./StaffDialog";

interface SetStageDialogProps {
  employee: QueueEmployee;
  onClose: () => void;
  returnFocusRef?: RefObject<HTMLElement | null>;
}

const REASON_MAX = 500;
const INPUT = "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm";

/**
 * "Set a different stage": the audited correction. Offers only the moves the
 * server accepts, and asks for what the move itself needs — the Clearinghouse
 * date and the photo — when the employee does not already have them.
 */
export function SetStageDialog({ employee, onClose, returnFocusRef }: SetStageDialogProps): React.ReactElement {
  const ids = useId();
  const [target, setTarget] = useState<EmployeeStatus | "">("");
  const [clearanceDate, setClearanceDate] = useState("");
  const [photoOnFile, setPhotoOnFile] = useState(false);
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const transition = useStaffTransition();
  const refresh = useRefreshQueues();

  const needsDate = target !== "" && stageNeedsClearanceDate(target, employee);
  const needsPhoto = target !== "" && stageNeedsPhoto(target, employee);
  const clearsDate = target !== "" && stageClearsClearance(target, employee);

  const check = (): string | null => {
    if (target === "") return "Choose the stage to move them to.";
    if (needsDate && !clearanceDate) return "Enter the clearance date shown in the Clearinghouse.";
    if (needsPhoto && !photoOnFile) return 'A badge stage needs a photo on file. Check "Photo on file?" once it is.';
    if (!reason.trim()) return "Enter a reason for this correction.";
    return null;
  };

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    const found = check();
    setProblem(found);
    if (found || target === "") return;
    transition.reset();
    const body: TransitionBody = {
      vendorId: employee.vendorId,
      etag: employee.etag,
      action: "set_stage",
      toStatus: target,
      reason: reason.trim(),
      ...(needsDate ? { clearanceDate } : {}),
      ...(needsPhoto ? { photoOnFile: true } : {}),
    };
    transition.mutate({ id: employee.id, body }, { onSuccess: onClose });
  };

  const reload = (): void => {
    transition.reset();
    void refresh();
    onClose();
  };

  return (
    <StaffDialog
      title={`Set a different stage for ${employee.firstName} ${employee.lastName}${
        employee.vendor ? ` (${employee.vendor.companyName})` : ""
      }`}
      onClose={onClose}
      returnFocusRef={returnFocusRef}
    >
      <form onSubmit={submit} noValidate className="space-y-4 text-sm">
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
          This is the audited correction path. Use it only to fix a mistake: the change, your name and your reason
          are recorded.
        </p>
        <p className="text-gray-700">Current stage: {STATUS_LABELS[employee.status]}</p>

        <label className="block font-medium text-gray-900" htmlFor={`${ids}-stage`}>
          New stage
        </label>
        <select
          id={`${ids}-stage`}
          value={target}
          onChange={(e) => setTarget(e.target.value as EmployeeStatus | "")}
          className={INPUT}
        >
          <option value="">Choose a stage</option>
          {setStageTargets(employee.status, employee).map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status]}
            </option>
          ))}
        </select>

        {clearsDate && employee.clearanceDate && (
          <p className="text-gray-700">
            Moving here removes the recorded clearance date ({formatCalendarDate(employee.clearanceDate)}). The
            correction keeps a record of it.
          </p>
        )}

        {needsDate && (
          <div>
            <label className="block font-medium text-gray-900" htmlFor={`${ids}-date`}>
              Clearance date shown in the Clearinghouse
            </label>
            <input
              id={`${ids}-date`}
              type="date"
              max={latestClearanceDate()}
              value={clearanceDate}
              onChange={(e) => setClearanceDate(e.target.value)}
              className={INPUT}
            />
          </div>
        )}

        {needsPhoto && (
          <label className="flex items-center gap-2 font-medium text-gray-900">
            <input type="checkbox" checked={photoOnFile} onChange={(e) => setPhotoOnFile(e.target.checked)} />
            Photo on file?
          </label>
        )}

        <div>
          <label className="block font-medium text-gray-900" htmlFor={`${ids}-reason`}>
            Reason for the correction
          </label>
          <input
            id={`${ids}-reason`}
            type="text"
            maxLength={REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={INPUT}
          />
        </div>

        {problem && (
          <p role="alert" className="text-red-700">
            {problem}
          </p>
        )}
        <ActionError error={transition.error} onReload={reload} />

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className={SECONDARY_BUTTON}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={transition.isPending}
            className={PRIMARY_BUTTON}
          >
            Set stage
          </button>
        </div>
      </form>
    </StaffDialog>
  );
}
